import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { DashboardData } from '@/models/DashboardData';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

/** Matches ChurnSignal.Api DashboardController: POST /workspaces/{id}/dashboard/details */
async function getDashboardDataByWorkspace(
  token: string | undefined,
  workspaceId: string,
  startTime: string,
  endTime: string,
): Promise<DashboardData> {
  const base = getBackendBaseUrl();
  const headers = backendRequestHeaders(token);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token for dashboard request.');
  }

  const url = `${base}/workspaces/${workspaceId}/dashboard/details`;
  const { data } = await axios.post<DashboardData>(
    url,
    { startTime, endTime },
    { headers },
  );

  return data;
}

export function useGetDashboardDataByWorkspace(startTime: string, endTime: string) {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const bearer = resolveBearerToken(sessionJwt);

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    // Try id first, then SK, then extract from SK if it starts with "WS#"
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      // If SK starts with "WS#", extract the UUID part
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3); // Remove "WS#" prefix
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);

  const finalStartTime = startTime.toLowerCase().includes('.')
    ? startTime.substring(0, startTime.indexOf('.'))
    : startTime;
  const finalEndTime = endTime.toLowerCase().includes('.') ? endTime.substring(0, endTime.indexOf('.')) : endTime;
  let validDate = false;
  if (!isNaN(new Date(startTime) as any) && !isNaN(new Date(endTime) as any)) {
    validDate = true;
  } else {
    validDate = false;
  }

  return useQuery<DashboardData>({
    queryKey: [queryKeys.accountMetrics, workspaceId, finalStartTime, finalEndTime],
    queryFn: () =>
      getDashboardDataByWorkspace(bearer || undefined, workspaceId, finalStartTime, finalEndTime),
    enabled: validDate && !!workspaceId && !!user && !!bearer,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
